import { useContext, useState } from 'react';
import { Flex, Box, Image, Icon, Tooltip } from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useNavigationConfig } from './useNavigationConfig';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { KulaTextLogo } from '@/assets/icons/colored';
import kulaLogo from '@/assets/icons/Logo.svg';
import { FlexAlign } from '@/assets/icons/nav-v2';
import NavigationContext from './context';
import { useKFlags } from '@/hooks/use-k-flags';
import { useIsMobile } from '@/hooks/use-IsMobile';
import { NavActivityTracker } from './components/NavActivityTracker';
import { useModeSyncWithUrl } from './hooks/useModeSyncWithUrl';
import { isNavItemActive } from './isNavItemActive';

export const NavItem = ({
  item,
  index,
  isCollapsed,
  wrapper: Wrapper = '',
}) => {
  const {
    component: Component,
    props,
    styles,
    isAllowedToExternalUsers,
  } = item;
  const isExternalUser = useIsExternalUser();

  const renderComponent = () => (
    <Component key={index} {...props} {...styles} isCollapsed={isCollapsed} />
  );

  return (
    <>
      {!isExternalUser &&
        item.isEnabled &&
        (Wrapper ? <Wrapper>{renderComponent()}</Wrapper> : renderComponent())}
      {isExternalUser &&
        isAllowedToExternalUsers &&
        item.isEnabled &&
        (Wrapper ? <Wrapper>{renderComponent()}</Wrapper> : renderComponent())}
    </>
  );
};

const NavItems = ({ config, styles, isCollapsed = false }) => {
  return (
    <Box {...styles}>
      {config?.map((item, index) => (
        <NavItem
          key={index}
          item={item}
          index={index}
          isCollapsed={isCollapsed}
        />
      ))}
    </Box>
  );
};

export const SideNavigation = () => {
  const [isFlexVisible, setFlexVisible] = useState(false);
  const { navItemsTopSection, navItemsMiddleSection, navBottomItems } =
    useNavigationConfig();
  const { isNavCollapsed, handleNavCollapse } = useContext(NavigationContext);
  const { ats_enabled } = useKFlags();
  useModeSyncWithUrl();

  // The scrollable and docked groups each find their own selection so the
  // bottom stays docked and any route item can notch.
  const { pathname } = useLocation();
  const topMiddleItems = [...navItemsTopSection, ...navItemsMiddleSection];
  const isItemActive = (i) => isNavItemActive(i, pathname);
  const topMiddleActiveIndex = topMiddleItems.findIndex(isItemActive);
  const bottomActiveIndex = navBottomItems.findIndex(isItemActive);

  // Splits a group around the selected item: before-panel, white notch,
  // after-panel. `fill` grows the trailing panel; `flat` keeps it flush.
  const renderNotchedGroup = (items, activeIndex, { fill, flat }) => {
    const notch = activeIndex >= 0;
    const before = notch ? items.slice(0, activeIndex) : items;
    const selected = notch ? items[activeIndex] : null;
    const after = notch ? items.slice(activeIndex + 1) : [];
    const beforeRadius = notch ? '0 0 16px 0' : flat ? '0' : '0 16px 0 0';
    return (
      <>
        {before.length > 0 && (
          <Box
            {...(notch || !fill ? { flexShrink: 0 } : { flex: '1', minH: 0 })}
            // flow-root contains the first item's top margin so no seam opens.
            display="flow-root"
            bg="secondary-25"
            borderRadius={beforeRadius}
            // Left gutter only; rows reach the right edge so highlights touch it.
            pl="4px"
            pr="0"
            py="0"
          >
            <NavItems config={before} isCollapsed={isNavCollapsed} />
          </Box>
        )}
        {selected && (
          // Above the before-panel so it covers an open notch's bottom curve.
          <Box flexShrink={0} position="relative" zIndex={2}>
            <NavItems config={[selected]} isCollapsed={isNavCollapsed} />
          </Box>
        )}
        {notch && (
          <Box
            {...(fill ? { flex: '1', minH: 0 } : { flexShrink: 0 })}
            display="flow-root"
            bg="secondary-25"
            borderRadius="0 16px 0 0"
            pl="4px"
            pr="0"
            py="0"
          >
            <NavItems config={after} isCollapsed={isNavCollapsed} />
          </Box>
        )}
      </>
    );
  };

  const isMobile = useIsMobile();

  useHotkeys(
    '[',
    () => {
      handleNavCollapse(!isNavCollapsed);
    },
    { enableOnTags: [] }
  );

  const renderOldSearchDesign = () => {
    return (
      <>
        {!isNavCollapsed ? (
          <Icon width="100px" height="30px" as={KulaTextLogo} />
        ) : (
          <Box position="relative">
            <Image
              src={kulaLogo}
              width="30px"
              height="30px"
              display={isFlexVisible ? 'none' : 'block'}
            />
            <Flex
              width="30px"
              height="30px"
              justifyContent="center"
              alignItems="center"
              bg="var(--kula-dark-secondary-50, #F4F4F5)"
              borderRadius="5px"
              display={!isFlexVisible ? 'none' : 'flex'}
              onClick={() => {
                handleNavCollapse(false);
                setFlexVisible(false);
              }}
            >
              <Tooltip
                label="Expand ( [ )"
                arrowSize={8}
                hasArrow
                placement="right"
                borderRadius="4px"
                variant="default"
              >
                <Flex justifyContent="center" alignItems="center">
                  <Icon
                    as={FlexAlign}
                    width="24px"
                    height="24px"
                    cursor="pointer"
                  />
                </Flex>
              </Tooltip>
            </Flex>
          </Box>
        )}
        {!isNavCollapsed && (
          <Tooltip
            label="Minimize ( [ )"
            arrowSize={8}
            hasArrow
            placement="right"
            borderRadius="4px"
            variant="default"
          >
            <Flex>
              <Icon
                as={FlexAlign}
                width="24px"
                height="24px"
                cursor="pointer"
                onClick={() => handleNavCollapse(true)}
              />
            </Flex>
          </Tooltip>
        )}
      </>
    );
  };

  return !isMobile ? (
    <NavigationContext.Consumer>
      {() => (
        <Flex
          direction="column"
          bg="white"
          // Dark mode swaps the nav's surface and panel colours (darkModeStyles).
          data-side-nav
          onMouseOver={() => setFlexVisible(true)}
          onMouseOut={() => setFlexVisible(false)}
        >
          <Flex
            as="aside"
            flexDir="column"
            height="100%"
            width={`${isNavCollapsed ? '58px' : '240px'}`}
            transition="width 0.25s"
            pt="0"
            pl="0"
            pr="0"
            pb={'0'}
            overflow="hidden"
            // `white` (not `base-white`): dark mode re-points it inside the
            // nav, so the strip behind the panels shows their notch curves.
            background="white"
          >
            {/* Logo / search — only in the pre-ats nav; empty otherwise. */}
            {!ats_enabled && (
              <Box flexShrink={0}>
                <Flex
                  justifyContent={!isNavCollapsed ? 'space-between' : 'center'}
                  alignItems="center"
                >
                  {renderOldSearchDesign()}
                </Flex>
              </Box>
            )}
            {/* Scrollable list group (top + middle) — notches in place. */}
            <Flex
              flex="1"
              minH={0}
              direction="column"
              overflowY="auto"
              overflowX="hidden"
            >
              {renderNotchedGroup(topMiddleItems, topMiddleActiveIndex, {
                fill: true,
                flat: false,
              })}
            </Flex>
            {/* Docked bottom group — flush with the list above (divider as the
                only separator, no card); notches in place when selected. */}
            <Box flexShrink={0} mb="8px">
              {renderNotchedGroup(navBottomItems, bottomActiveIndex, {
                fill: false,
                flat: true,
              })}
              {/* Activity tracker for bulk operations - gets data from context */}
              <Box bg="secondary-25" px="4px">
                <NavActivityTracker isCollapsed={isNavCollapsed} />
              </Box>
            </Box>
          </Flex>
        </Flex>
      )}
    </NavigationContext.Consumer>
  ) : null;
};
